import UPFileInput from 'components/file-input';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  JOB_APPLICATION__CV_HINT,
  JOB_APPLICATION__CV_LABEL,
  JOB_APPLICATION__CV_PLACEHOLDER,
  JOB_APPLICATION__CV_SIZE_ERROR,
  JOB_APPLICATION__CV_TYPE_ERROR,
} from 'translations/constants';
import { FieldHint, FieldRow } from './styles';
import { CV_ACCEPT_ATTRIBUTE, CvRejectionReason, validateCvFile } from './validation';

export interface CvUploadProps {
  file: File | null;
  onFileChange: (file: File | null) => void;
  /** Set when the server rejected the file we thought was fine — it revalidates and wins. */
  serverRejection: CvRejectionReason | null;
}

const CvUpload = (props: CvUploadProps): JSX.Element => {
  const { file, onFileChange, serverRejection } = props;
  const [t] = useTranslation();

  // Held locally, not derived from `file`: a rejected file is never handed up, so there would be
  // nothing left to re-derive the rejection from.
  const [localRejection, setLocalRejection] = useState<CvRejectionReason | null>(null);
  const rejection = localRejection ?? serverRejection;

  /** UPFileInput sends a synthetic `{ target: { files: [] } }` on clear, so both paths land here. */
  const handleChange = (event: { target: { files?: FileList | File[] | null } }) => {
    const selected = event.target.files?.[0] ?? null;

    if (!selected) {
      setLocalRejection(null);
      onFileChange(null);
      return;
    }

    const reason = validateCvFile(selected);
    setLocalRejection(reason);
    // Rejected files are never handed upward, so a bad pick cannot ride along on submit.
    onFileChange(reason ? null : selected);
  };

  const errorText = rejection === 'size' ? t(JOB_APPLICATION__CV_SIZE_ERROR) : t(JOB_APPLICATION__CV_TYPE_ERROR);

  return (
    <FieldRow>
      <UPFileInput
        value={file?.name ?? ''}
        onChange={handleChange}
        label={t(JOB_APPLICATION__CV_LABEL)}
        inputName="cv"
        placeholder={t(JOB_APPLICATION__CV_PLACEHOLDER)}
        // Differs from the input's default set: .doc is allowed here, .png / .gif / .webp are not.
        accept={CV_ACCEPT_ATTRIBUTE}
        clearable
        error={!!rejection}
        helperText={rejection ? errorText : ''}
      />
      {/* Must not claim the file is scanned: content inspection is not implemented on either side. */}
      <FieldHint>{t(JOB_APPLICATION__CV_HINT)}</FieldHint>
    </FieldRow>
  );
};

export default CvUpload;
